import { Championship } from "api";
import MaterialTable from "material-table";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { BoxCard, FormButton, LoggedUserContext } from "ui/componentes";
import { LocalizationPTBr } from "util/MaterialTableUtil";
import { ChampionshipDriver } from "../../../api";
import { useSnackbar } from "notistack";
import { functions } from "../../../config/firebaseConfig";
import { SafeDriversCalcInfo } from "./SafeDriversCalcInfo";

interface UseUpdatePenaltyPointsResults {
    updateSafeDrivers: (championship: Championship) => void;
    updating: boolean;
}

const useUpdateSafeDrivers = (): UseUpdatePenaltyPointsResults => {
    const [updating, setUpdating] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const updateSafeDrivers = useCallback(
        (championship: Championship) => {
            setUpdating(true);
            functions
                .httpsCallable("safe_drivers-calculateSafeDriver")({
                    championshipId: championship.id,
                })
                .then(() => {
                    enqueueSnackbar(
                        "Piloto mais seguro atualizado com sucesso!",
                        {
                            variant: "success",
                        }
                    );
                })
                .catch(() => {
                    enqueueSnackbar(
                        "Não possível atualizar o piloto mais seguro!",
                        { variant: "error" }
                    );
                })
                .finally(() => setUpdating(false));
        },
        [enqueueSnackbar]
    );

    return {
        updateSafeDrivers,
        updating,
    };
};

interface SafeDriversTableProps {
    championship: Championship;
    drivers: ChampionshipDriver[];
}

const SafeDriversTable: React.FC<SafeDriversTableProps> = ({
    championship,
    drivers,
}) => {
    const loggedUser = useContext(LoggedUserContext);

    const {
        updating: updatingSafeDrivers,
        updateSafeDrivers,
    } = useUpdateSafeDrivers();

    const driversByIRacingId = useMemo(() => {
        return drivers.reduce(
            (previousValue, currentValue) => ({
                ...previousValue,
                [currentValue.iracingId || ""]: currentValue.displayName,
            }),
            {}
        );
    }, [drivers]);

    return (
        <MaterialTable
            title={`Piloto mais seguro`}
            components={{
                Container: BoxCard,
            }}
            columns={[
                {
                    field: "name",
                    title: "Piloto",
                    render: (data) => driversByIRacingId[data.iRacingId],
                    cellStyle: () => ({
                        padding: "6px 8px 6px 8px",
                    }),
                },
                {
                    field: "lapsComplete",
                    title: "Voltas",
                    align: "right",
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "percentageOfMaxLapsComplete",
                    title: "% Voltas",
                    align: "right",
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                    render: (data) => `${data.percentageOfMaxLapsComplete}%`,
                },
                {
                    field: "incidents",
                    title: "Incidentes",
                    align: "right",
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "percentageIncidents",
                    title: "% Incidentes",
                    align: "right",
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                    render: (data) => `${data.percentageIncidents}%`,
                },
            ]}
            data={championship.safeDrivers || []}
            localization={LocalizationPTBr}
            actions={[
                {
                    isFreeAction: true,
                    icon: () => <SafeDriversCalcInfo />,
                    onClick: () => "",
                },
                {
                    isFreeAction: true,
                    icon: () => (
                        <FormButton
                            color="primary"
                            loading={updatingSafeDrivers}
                            onClick={() => {
                                updateSafeDrivers(championship);
                            }}
                        >
                            Recalcular piloto mais seguro
                        </FormButton>
                    ),
                    onClick: () => "",
                    disabled: updatingSafeDrivers,
                    hidden: !loggedUser.isAdmin,
                },
            ]}
            options={{
                pageSize: 5,
                draggable: false,
                sorting: false,
                search: false,
                rowStyle: () => ({
                    padding: 0,
                    fontSize: "0.857rem",
                }),
            }}
        />
    );
};

export { SafeDriversTable };
